import {
  apiRecordThumbnailEmotionconId,
} from "../api/backend/user";


/**
 * Function to fetch the current or source user's details.
 * @param {Object} params - Parameters to fetch user details.
 * @param {string} params.currentMemberId - The ID of the currently logged-in user.
 * @param {string} params.userId - The ID of the user to fetch details for.
 * @param {function} params.setCurrentMemberData - State setter for updating profile data.
 * @param {function} params.setAccountHolder - State setter for defining account ownership.
 * @param {function} params.setThumbnailPreference - State setter for updating thumbnail preference.
 * @param {function} params.setCurrentEmotionId - State setter for updating selected emoji.
 * @param {function} params.setLoading - State setter to toggle loading status.
 * @param {function} params.fetchDetailsOnProfileClick - Function to fetch details of the current user.
 * @param {function} params.getSourceAccountHolderDetailsById - Function to fetch details of another user by ID.
 * @param {function} params.setError - State setter for managing error status.
 * @param {function} params.setSkitsData - State setter for resetting skits data.
 * @param {function} params.setNoSkitsAvailable - State setter for handling skits availability.
 */
export const fetchUserDetails = async (args) => {
  const {
    currentMemberId,
    userId,
    setCurrentMemberData,
    setAccountHolder,
    setThumbnailPreference,
    setCurrentEmotionId,
    setLoading,
    fetchDetailsOnProfileClick,
    getSourceAccountHolderDetailsById,
    setError,
    setSkitsData,
    setNoSkitsAvailable,
    handleLogout,
    notificationsCountFetchedFromMiddleware, // ✅ NEW
  } = args;

  if (!currentMemberId) return;

  setLoading(true);
  try {
    setError(null);
    setSkitsData([]);
    setNoSkitsAvailable(false);

    let userDetails = null;
    if (userId === currentMemberId) {
      userDetails = await fetchDetailsOnProfileClick();
      setAccountHolder(true);
    } else {
      userDetails = await getSourceAccountHolderDetailsById(userId);
      setAccountHolder(false);
    }

    // ✅ NEW: hydrate notifications (supports both shapes)
    if (typeof notificationsCountFetchedFromMiddleware === "function") {
       if (typeof userDetails?.unreadNotificationCount === "number") {
        notificationsCountFetchedFromMiddleware(userDetails.unreadNotificationCount);
        }
     }
    // ✅ END NEW

    // eject guard
    if (userDetails?.__eject) {
      await handleLogout?.();
      return;
    }

    if (userDetails && typeof userDetails === "object") {
      setCurrentMemberData(userDetails);
      setThumbnailPreference(userDetails.thumbnailPreference ?? "default");
      setCurrentEmotionId(userDetails.emotionconId ?? null);
    } else {
      if (!currentMemberId) return;
      setError("Failed to load user details.");
    }
  } catch (error) {
    if (!currentMemberId) return;
    console.error("Error fetching user details:", error);

      // Optional: count-only hydration from error payloads (no lists)
     try {
        const maybeCount =
         error?.response?.data?.unreadNotificationCount ??
          error?.unreadNotificationCount ??
          null;
        if (
        typeof notificationsCountFetchedFromMiddleware === "function" &&
          typeof maybeCount === "number"
       ) {
          notificationsCountFetchedFromMiddleware(maybeCount);
       }
     } catch (_) {}

    setError("An error occurred while loading user details.");
  } finally {
    setLoading(false);
  }
};
/**

/**
 * Selects an emoji and updates it in the user profile, then refreshes user details.
 * @param {Object} params - Parameters for handling emoji selection.
 */
export const handleSelectEmoji = async ({
  userId,
  emotionconId,
  currentEmotionId,
  setCurrentEmotionId,
  setShowAllEmojis,
  fetchUserDetails,
}) => {
  if (emotionconId !== currentEmotionId) {
    setCurrentEmotionId(emotionconId);
    setShowAllEmojis(false);
  }
  try {
    await apiRecordThumbnailEmotionconId(userId, emotionconId);
    fetchUserDetails();
  } catch (error) {
    console.error("Error updating emotionconId:", error);
  }
};
